import {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    useCallback,
} from "react";
import { motion, useAnimationControls } from "framer-motion";

const ThemeContext = createContext({ theme: "dark", toggle: () => {} });
export const useTheme = () => useContext(ThemeContext);

const AUTO_INTERVAL = 45000; // 45s
const ROLL_DOWN = 1.25; // paint coat rolls down
const REVEAL = 0.55; // coat dissolves to show freshly-painted theme

function BigRoller({ size = 150 }) {
    return (
        <svg viewBox="0 0 260 220" width={size} height={size}
            style={{ filter: "drop-shadow(0 12px 18px rgba(0,0,0,0.45))" }}>
            <rect x="18" y="188" width="52" height="22" rx="8" fill="#111" stroke="#fff" strokeWidth="1.5" />
            <line x1="46" y1="188" x2="132" y2="98" stroke="#fff" strokeWidth="7" strokeLinecap="round" />
            <path d="M 132 98 L 150 82 L 224 82 L 224 100 L 150 100 Z" fill="none" stroke="#fff" strokeWidth="4" />
            <ellipse cx="150" cy="92" rx="8" ry="16" fill="#0A0A0A" stroke="#fff" strokeWidth="2" />
            <ellipse cx="224" cy="92" rx="8" ry="16" fill="#0A0A0A" stroke="#fff" strokeWidth="2" />
            <rect x="150" y="76" width="74" height="32" rx="2" fill="#fff" stroke="#fff" strokeWidth="2" />
        </svg>
    );
}

export function ThemeProvider({ children }) {
    const [theme, setTheme] = useState("dark");
    const [coatColors, setCoatColors] = useState({
        bg: "#FFFFFF",
        fg: "#0A0A0A",
    });
    const coat = useAnimationControls();
    const edge = useAnimationControls();
    const label = useAnimationControls();
    const running = useRef(false);
    const [painting, setPainting] = useState(false);

    useEffect(() => {
        document.documentElement.setAttribute("data-theme", theme);
    }, [theme]);

    const runPaint = useCallback(() => {
        // Theme flips without the paint-wipe overlay — a clean crossfade.
        setTheme((t) => (t === "dark" ? "light" : "dark"));
    }, []);

    const runRef = useRef(runPaint);
    runRef.current = runPaint;

    const toggle = useCallback(() => runRef.current(), []);

    useEffect(() => {
        const id = setInterval(() => runRef.current(), AUTO_INTERVAL);
        return () => clearInterval(id);
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, toggle }}>
            {children}

            {/* PAINT COAT — the fresh theme rolled onto the page */}
            <motion.div
                aria-hidden
                data-testid="theme-paint-wipe"
                initial={{ height: 0, opacity: 1 }}
                animate={coat}
                className="fixed top-0 left-0 right-0 z-[90] pointer-events-none overflow-hidden"
                style={{
                    background: coatColors.bg,
                    willChange: "height, opacity",
                    visibility: painting ? "visible" : "hidden",
                }}
            >
                {/* faint brand watermark in the fresh coat */}
                <div className="absolute inset-0 flex items-start justify-center pt-[35vh]">
                    <motion.span
                        initial={{ opacity: 0 }}
                        animate={label}
                        className="font-display uppercase text-6xl md:text-8xl tracking-tighter select-none"
                        style={{ color: coatColors.fg }}
                    >
                        PaintKing
                    </motion.span>
                </div>
            </motion.div>

            {/* WET LEADING EDGE + high-res drips + roller — rides down ahead of the coat */}
            <motion.div
                aria-hidden
                initial={{ opacity: 0 }}
                animate={edge}
                className="fixed top-0 left-0 right-0 z-[92] pointer-events-none"
                style={{
                    willChange: "transform, opacity",
                    visibility: painting ? "visible" : "hidden",
                }}
            >
                {/* thin rainbow wet band */}
                <div
                    className="absolute left-0 right-0 -top-2 h-3"
                    style={{
                        background:
                            "linear-gradient(90deg,#FF2BD6,#FF4500,#FFE600,#00FF85,#00E5FF,#A855F7)",
                    }}
                />
                {/* high-res glossy drips hanging off the wet edge */}
                <img
                    src="/paint/drips.webp"
                    alt=""
                    className="absolute left-0 right-0 -top-1 w-full"
                    style={{
                        height: "240px",
                        objectFit: "fill",
                        filter: "saturate(1.1) drop-shadow(0 12px 18px rgba(0,0,0,0.35))",
                    }}
                />
                {/* the roller riding the wet edge */}
                <div className="absolute -top-24" style={{ right: "8%" }}>
                    <BigRoller size={150} />
                </div>
                <div className="absolute -top-20" style={{ left: "16%" }}>
                    <BigRoller size={120} />
                </div>
            </motion.div>
        </ThemeContext.Provider>
    );
}
